import { oneLine } from 'common-tags';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import avatarPlaceholder from 'assets/avatar-placeholder.svg';
import { CalendarEventDownloadButton } from 'components/CalendarEventDownloadButton';
import { CarouselBackButton } from 'components/CarouselBackButton';
import { Phrase, interpolateString } from 'components/Phrase';
import { useStringContext } from 'components/StringContext';
import { CalendarService, DateTimeFormat, IState } from 'typings';

import {
  Container,
  Description,
  FaceRemote,
  FaceUnknown,
  Header,
  Note,
  SalesPerson,
  SummaryDetails,
  SummaryImage,
  SummaryItem,
  SummaryItems,
} from './styles';

export const CarouselItemConfirmFinishSalesTop = () => {
  const {
    addressTimezone = moment.tz.guess(),
    appointmentId = '',
    city = '',
    clientKey,
    selectedTimeSlot,
    state = '',
    street1 = '',
    street2 = '',
    zipCode = '',
  } = useSelector(({ client, userInput }: IState) => ({
    addressTimezone: userInput.address.timezone,
    appointmentId: userInput.appointmentId,
    city: userInput.address.city,
    clientKey: client.key,
    selectedTimeSlot: userInput.selectedTimeSlot,
    state: userInput.address.state,
    street1: userInput.address.street1,
    street2: userInput.address.street2,
    zipCode: userInput.address.zipCode,
  }));
  const strings = useStringContext();

  const {
    contact = null,
    endTime = '',
    label = null,
    startTime = '',
  } = selectedTimeSlot || {};

  const avatarMediaId = contact?.avatarMediaId || '';
  const firstName = contact?.firstName || '';

  const eventDescription = contact
    ? interpolateString(
        strings['CONFIRM_FINISH_SALES.EVENT.DESCRIPTION.TEMPLATE'] ?? '',
        {
          FIRST_NAME: firstName,
        },
      )
    : strings['CONFIRM_FINISH_SALES.EVENT.DESCRIPTION.NO_CONTACT'];

  const fileNameDateString = moment
    .tz(startTime, addressTimezone)
    .format(DateTimeFormat.DATE_ICS_FILE_NAME);
  const event = {
    appointmentId,
    description: eventDescription ?? '',
    endTime,
    fileName: `${clientKey}-${fileNameDateString}`,
    location: oneLine`
      ${street1},
      ${street2 ? `${street2},` : ''}
      ${city}, ${state} ${zipCode}
    `,
    startTime,
    title: strings['CONFIRM_FINISH_SALES.EVENT.TITLE'] ?? '',
  };

  return (
    <>
      {process.env.NODE_ENV === 'development' ? <CarouselBackButton /> : null}
      <Header>
        <Phrase id="CONFIRM_FINISH_SALES.HEADER" />
      </Header>
      <Container>
        <SummaryDetails>
          <SummaryItems>
            {contact && (
              <SalesPerson>
                {avatarMediaId ? (
                  <FaceRemote alt={firstName} mediaId={avatarMediaId} />
                ) : (
                  <FaceUnknown alt={firstName} src={avatarPlaceholder} />
                )}
                <Description>
                  <Phrase
                    FIRST_NAME={firstName}
                    id="CONFIRM_FINISH_SALES.DESCRIPTION.HTML.TEMPLATE"
                  />
                </Description>
              </SalesPerson>
            )}
            {!label && (
              <>
                <SummaryItem>
                  <SummaryImage />
                  <span>
                    <Phrase id="CONFIRM_FINISH_SALES.CALENDAR.LABEL" />
                  </span>
                </SummaryItem>
                <CalendarEventDownloadButton
                  calendarService={CalendarService.GOOGLE}
                  event={event}
                />
                <CalendarEventDownloadButton
                  calendarService={CalendarService.APPLE}
                  event={event}
                />
                <CalendarEventDownloadButton
                  calendarService={CalendarService.OUTLOOK}
                  event={event}
                />
              </>
            )}
            <Note>
              <Phrase id="CONFIRM_FINISH_SALES.CALENDAR.NOTE" />
            </Note>
          </SummaryItems>
        </SummaryDetails>
      </Container>
    </>
  );
};
